import {useState, useCallback, type ReactNode} from 'react'
import {announce} from '@github-ui/aria-live'
import {ChatInputDisplay} from './ChatInputDisplay'
import {CLIChatInputHeader} from './CLIChatInputHeader'
import {CLIStopButton} from './CLIStopButton'
import {PromptInput} from './PromptInput'
import {useCreateCommand} from '../../hooks/useCreateCommand'
import {usePromptResponseSubmission} from '../../hooks/use-prompt-response-submission'
import {computeConnectionStatus} from '../../utils/connection-status'
import {isActiveSession} from '../../types/session'
import type {Session} from '../../types/session'
import {isInternalStaff} from '../../utils/is-internal-staff'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {BlockedTaskInput} from './BlockedTaskInput'
import styles from './ChatInputDisplay.module.css'

interface CLIChatInputProps {
  /** The CLI session */
  session: Session
  /** Whether the CLI session supports remote steering. When false, shows a blocked message. */
  remoteSteerable?: boolean
  /** Whether the remote control (steering) policy is enabled by the org. When false, shows admin-contact message. */
  remoteControlPolicyEnabled?: boolean
  /** SSO organization IDs for the current user. */
  ssoOrgIds?: string[]
  /** The agent task ID used in the CLI resume command */
  agentTaskId?: string
  /** The task-level stale_at deadline for computing connection status */
  staleAt?: string
  /** Timestamp of the last successful task query fetch (React Query dataUpdatedAt) */
  dataUpdatedAt?: number
  /** Callback when a command is successfully sent */
  onCommandSent?: () => void
  /** Whether the CLI remote session notice has been dismissed (from route query) */
  cliRemoteNoticeDismissed?: boolean
  /** Whether the task is shared — suppresses privacy banners for viewers */
  isShared?: boolean
  /** Optional profile-specific controls rendered in the chat input toolbar. */
  toolbarSlot?: ReactNode
  /** App sessions use the remote controls but cannot be resumed from a standalone CLI. */
  supportsCLICommands?: boolean
}

/**
 * Input component for CLI sessions.
 * Shows PromptInput when there's a pending prompt, otherwise shows ChatInputDisplay.
 * Handles both regular message submission and prompt response submission.
 */
export function CLIChatInput({
  session,
  remoteSteerable,
  remoteControlPolicyEnabled,
  ssoOrgIds,
  agentTaskId,
  staleAt,
  dataUpdatedAt,
  onCommandSent,
  cliRemoteNoticeDismissed,
  isShared,
  toolbarSlot,
  supportsCLICommands = true,
}: CLIChatInputProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, session.state, dataUpdatedAt)
  const sessionId = session.id
  const [value, setValue] = useState('')
  const [failedToSubmit, setFailedToSubmit] = useState(false)

  const handleSubmitError = useCallback(() => {
    setFailedToSubmit(true)
    announce('Failed to submit latest message')
  }, [])

  const {
    pendingPrompt,
    pendingPromptIndex,
    pendingPromptCount,
    isLoading,
    isPending: isPromptResponsePending,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
    handlePromptRespond,
  } = usePromptResponseSubmission({
    sessionId,
    taskId: session.task_id,
    enabled: true,
    onError: handleSubmitError,
  })

  const {createCommand, isPending} = useCreateCommand({
    onError: handleSubmitError,
  })

  const handleSubmit = useCallback(() => {
    if (!value.trim() || isPending) return

    setFailedToSubmit(false)
    createCommand({
      sessionId,
      content: value.trim(),
    })

    setValue('')
    onCommandSent?.()
  }, [value, isPending, sessionId, createCommand, onCommandSent])

  const handleChange = useCallback((nextValue: string) => {
    setValue(nextValue)
    setFailedToSubmit(false)
  }, [])

  if (!isActiveSession(session)) {
    const resumeCommand = agentTaskId ? `copilot --remote --resume ${agentTaskId}` : 'copilot --remote --resume'
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>
          {supportsCLICommands ? (
            <>
              Session has ended. Reconnect with <strong>{resumeCommand}</strong>
            </>
          ) : (
            'Session has ended. Reconnect from the Copilot App.'
          )}
        </BlockedTaskInput>
      </div>
    )
  }

  // Relax the policy gate only for internal staff (GitHub or Microsoft) whose session the backend already marked steerable; fails closed otherwise. MC stays the enforcement boundary.
  const staffSteeringAllowed = isInternalStaff(ssoOrgIds) && remoteSteerable === true

  if (remoteControlPolicyEnabled === false && !staffSteeringAllowed) {
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>
          Steering disabled. Contact your administrator to enable steering on local sessions.
        </BlockedTaskInput>
      </div>
    )
  }

  if (remoteSteerable === false) {
    return (
      <>
        <CLIChatInputHeader
          agentTaskId={agentTaskId}
          connectionStatus={connectionStatus}
          isShared={isShared}
          supportsCLICommands={supportsCLICommands}
        />
        <div className={styles.inputWrapper}>
          <BlockedTaskInput>
            {supportsCLICommands ? (
              <>
                Session synced to your account. Type <strong>/remote on</strong> to enable steering on this session. To
                enable steering for a new session, start it with <strong>copilot --remote</strong>
              </>
            ) : (
              'Steering is not available for this session.'
            )}
          </BlockedTaskInput>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.skeletonWrapper}>
          <LoadingSkeleton variant="rounded" height="100%" width="100%" />
        </div>
      </div>
    )
  }

  if (pendingPrompt) {
    return (
      <>
        <CLIChatInputHeader
          agentTaskId={agentTaskId}
          connectionStatus={connectionStatus}
          cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
          isShared={isShared}
          supportsCLICommands={supportsCLICommands}
        />
        <PromptInput
          prompt={pendingPrompt}
          promptNavigation={{
            current: pendingPromptIndex + 1,
            total: pendingPromptCount,
            onPrevious: goToPreviousPrompt,
            onNext: goToNextPrompt,
          }}
          onRespond={handlePromptRespond}
          onDismiss={dismissPrompt}
          disabled={isPromptResponsePending}
        />
        {failedToSubmit && <div className="mt-2 color-fg-danger text-small">Failed to submit latest message</div>}
      </>
    )
  }

  return (
    <>
      <CLIChatInputHeader
        agentTaskId={agentTaskId}
        connectionStatus={connectionStatus}
        cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
        isShared={isShared}
        supportsCLICommands={supportsCLICommands}
      />
      <ChatInputDisplay
        value={value}
        onChange={handleChange}
        onSubmit={handleSubmit}
        placeholder="Send a message to the CLI session..."
        isSubmitting={isPending}
        toolbarSlot={toolbarSlot}
        stopSlot={<CLIStopButton session={session} />}
        supportsShellCommands
      />
      {failedToSubmit && <div className="mt-2 color-fg-danger text-small">Failed to submit latest message</div>}
    </>
  )
}
